// ============================================================================
// CONTACT PICKER COMPONENT
// React-Select based searchable contact picker with type filtering
// Date: October 17, 2025
// Updated: December 5, 2025 - Fixed to load selected contact on mount
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Select, { type SingleValue, type StylesConfig } from 'react-select';
import { authService } from '@/lib/authService';

// ============================================================================
// TYPES
// ============================================================================

interface Contact {
  id: number;
  company_name: string;
  contact_person?: string;
  email?: string;
  contact_type: string;
  phone?: string;
  mobile?: string;
}

interface ContactPickerProps {
  value?: number | null;
  onChange: (contactId: number | null, contact: Contact | null) => void;
  contactType?: 'customer' | 'supplier' | 'vendor' | 'all';
  placeholder?: string;
  className?: string;
  isDisabled?: boolean;
  required?: boolean;
}

interface ContactOption {
  value: number;
  label: string;
  contact: Contact;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function ContactPicker({
  value,
  onChange,
  contactType = 'all',
  placeholder = 'Search contacts...',
  className = '',
  isDisabled = false,
  required = false
}: ContactPickerProps) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Load a specific contact by ID (for when we have a pre-selected value)
  const loadContactById = async (contactId: number) => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return null;
      
      const response = await fetch(`/api/v1/contacts/${contactId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success && data.data) {
        return data.data as Contact;
      }
    } catch (error) {
      console.error('Error loading contact by ID:', error);
    }
    return null;
  };
  
  // Load contacts
  const loadContacts = useCallback(async (search: string = '') => {
    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('No auth token');
      }
      
      const url = new URL('/api/v1/contacts', window.location.origin);
      if (contactType !== 'all') {
        url.searchParams.set('type', contactType);
      }
      if (search) {
        url.searchParams.set('search', search);
      }
      url.searchParams.set('active', 'true'); // Only active contacts
      url.searchParams.set('limit', '100');
      
      const response = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        let loadedContacts = data.data || [];
        
        // If we have a selected contact that's not in the list, add it
        if (selectedContact && !loadedContacts.find((c: Contact) => c.id === selectedContact.id)) {
          loadedContacts = [selectedContact, ...loadedContacts];
        }
        
        setContacts(loadedContacts);
      } else {
        console.error('Failed to load contacts:', data.error);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  }, [contactType, selectedContact]);
  
  // Load selected contact on mount if value is provided
  useEffect(() => {
    const initSelectedContact = async () => {
      if (value && value !== selectedContact?.id) {
        const contact = await loadContactById(value);
        if (contact) {
          setSelectedContact(contact);
        }
      }
    };
    initSelectedContact();
  }, [value, selectedContact?.id]);
  
  // Initial load
  useEffect(() => {
    loadContacts();
  }, [loadContacts]);
  
  // Handle search input
  const handleInputChange = (newValue: string) => {
    setSearchTerm(newValue);
    if (newValue.length >= 2) {
      loadContacts(newValue);
    } else if (newValue.length === 0) {
      loadContacts();
    }
  };
  
  // Format options for react-select
  const formatContactLabel = (c: Contact) => c.company_name 
    ? `${c.company_name}${c.contact_person ? ` (${c.contact_person})` : ''}`
    : c.contact_person || 'Unknown';
    
  const options: ContactOption[] = contacts.map(c => ({
    value: c.id,
    label: formatContactLabel(c),
    contact: c
  }));
  
  // Find selected option - use selectedContact state as fallback
  let selectedOption = options.find(o => o.value === value);
  if (!selectedOption && selectedContact && selectedContact.id === value) {
    selectedOption = {
      value: selectedContact.id,
      label: formatContactLabel(selectedContact),
      contact: selectedContact
    };
  }
  
  // Custom styles for react-select
  const customStyles: StylesConfig<ContactOption, false> = {
    control: (base, state) => ({
      ...base,
      minHeight: '42px',
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
        ? '#eff6ff' 
        : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      '&:active': {
        backgroundColor: '#3b82f6'
      }
    })
  };
  
  return (
    <div className={className}>
      <Select<ContactOption, false>
        value={selectedOption}
        onChange={(option: SingleValue<ContactOption>) => {
          onChange(option?.value || null, option?.contact || null);
        }}
        options={options}
        isLoading={loading}
        onInputChange={handleInputChange}
        placeholder={placeholder}
        isClearable={!required}
        isSearchable
        isDisabled={isDisabled}
        styles={customStyles}
        noOptionsMessage={() => 
          searchTerm.length > 0 
            ? 'No contacts found' 
            : 'Start typing to search...'
        }
        loadingMessage={() => 'Loading contacts...'}
      />
    </div>
  );
}
